import React, { useEffect, useState } from 'react'
import { Form } from 'formik'
import { FormColumn, FormRow, TitleBar } from 'components/Shared/CrudComponents'
import { useTranslation } from 'react-i18next'
import { createValidationSchema } from './form'
import { FormikForm } from 'components/Shared/FormikForm'
import { GROUP_DATA_WRITE, useAuth } from 'context/auth'
import { Label } from 'components/Shared/Label'
import { IndependentFieldComponent } from 'components/Shared/IndependentFieldComponent'
import { SelectUsersField } from 'components/Shared/SelectUsersField'
import { addGroupMember, deleteGroupMember } from 'services/GroupsService'
import { EXTRA, MAIN } from './MembershipType'

export default function GroupsForm ({
  initialValues,
  initialMembers,
  onSubmit,
  title,
  requiredPrivileges
}) {
  const [mainMembers, setMainMembers] = useState(initialMembers?.main)
  const [extraMembers, setExtraMembers] = useState(initialMembers?.extra)
  const { hasAnyPrivileges } = useAuth()

  useEffect(() => {
    setMainMembers(initialMembers?.main)
    setExtraMembers(initialMembers?.extra)
  }, [initialMembers])

  const { t } = useTranslation()

  const deleteRemovedMembers = (groupId, initialMembersIds, selectedMembersIds) => {
    const memberIdsToRemove = initialMembersIds.filter(initialId => !selectedMembersIds.includes(initialId))
    return memberIdsToRemove.map(userId => deleteGroupMember(groupId, userId))
  }

  const createAddedMembers = (groupId, initialMembersIds, selectedMembersIds, type) => {
    const memberIdsToAdd = selectedMembersIds.filter(selectedId => !initialMembersIds.includes(selectedId))
    return memberIdsToAdd.map(userId => addGroupMember(groupId, { userId, membershipType: type }))
  }

  const handleMembersChange = (groupId) => {
    const initialMainIds = initialMembers?.main?.map(member => member.userId)
    const initialExtraIds = initialMembers?.extra?.map(member => member.userId)
    const selectedMainIds = mainMembers.map(member => member.userId)
    const selectedExtraIds = extraMembers.map(member => member.userId)
    return Promise.all([
      ...deleteRemovedMembers(groupId, initialMainIds, selectedMainIds),
      ...deleteRemovedMembers(groupId, initialExtraIds, selectedExtraIds),
    ])
      .then(() =>
        Promise.all([
          ...createAddedMembers(groupId, initialMainIds, selectedMainIds, MAIN),
          ...createAddedMembers(groupId, initialExtraIds, selectedExtraIds, EXTRA)])
      )
  }

  const onMainMembersChange = (members) => {
    const membersIds = members.map(m => m.userId)
    setExtraMembers(oldExtraMembers => oldExtraMembers.filter(member => !membersIds.includes(member.userId)))
    setMainMembers(members)
  }

  const onExtraMembersChange = (members) => {
    const membersIds = members.map(m => m.userId)
    setMainMembers(oldMainMembers => oldMainMembers.filter(member => !membersIds.includes(member.userId)))
    setExtraMembers(members)
  }

  return (
    <>
      <FormikForm
        initialValues={initialValues}
        validationSchema={createValidationSchema()}
        validateOnChange={false}
        validateOnBlur
        enableReinitialize
        preventNavigation={false}
        onSubmit={groupValues => onSubmit(groupValues, handleMembersChange)}>
        <Form>
          <TitleBar
            buttonLabel={t('groups.form.submit')}
            title={title}
            isButtonDisabled={!hasAnyPrivileges(requiredPrivileges)}/>
          <FormRow>
            <FormColumn className={'w-100'} disabled={!hasAnyPrivileges([GROUP_DATA_WRITE])}>
              <Label text={'groups.form.name'}/>
              <IndependentFieldComponent fieldName="name"/>
              <Label text={'groups.form.mainMembers'}/>
              <SelectUsersField
                members={mainMembers}
                onMembersChanged={onMainMembersChange}
              />
              <Label text={'groups.form.extraMembers'}/>
              <SelectUsersField
                members={extraMembers}
                onMembersChanged={onExtraMembersChange}
              />
            </FormColumn>
          </FormRow>
        </Form>
      </FormikForm>
    </>
  )
}
